/**
 * Macro Calculator
 * Multi-step wizard for creating data-driven nutrition plans
 */

import { useState, useMemo, useEffect } from 'react';
import { Calculator, ChevronLeft, ChevronRight, Check, Scale, Target, Utensils, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
  calculateBMR,
  calculateTDEE,
  calculateFullPlan,
  validatePlan,
  formatWeeklyChange,
  formatCalorieAdjustment,
  ACTIVITY_LABELS,
  GOAL_LABELS,
  CALORIE_PRESETS,
  type ClientProfile,
  type GoalSettings,
  type Goal,
  type ActivityLevel,
  type CalculationResult,
} from '@/lib/nutrition/macro-calculator';
import type { BaselineAnalysis } from '@/lib/nutrition/macro-insights';
import {
  useClientForCalculator,
  useBaselineAnalysis,
  useCreateMacroPlan,
  useSaveClientProfile,
} from '@/hooks/use-macro-calculator';

// =============================================================================
// Types
// =============================================================================

interface MacroCalculatorProps {
  clientId: string;
  clientName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
}

type Step = 'profile' | 'baseline' | 'goal' | 'macros' | 'review';

const STEPS: { id: Step; label: string; icon: React.ReactNode }[] = [
  { id: 'profile', label: 'Profile', icon: <Scale className="h-4 w-4" /> },
  { id: 'baseline', label: 'Baseline', icon: <Calculator className="h-4 w-4" /> },
  { id: 'goal', label: 'Goal', icon: <Target className="h-4 w-4" /> },
  { id: 'macros', label: 'Macros', icon: <Utensils className="h-4 w-4" /> },
  { id: 'review', label: 'Review', icon: <Check className="h-4 w-4" /> },
];

// =============================================================================
// Component
// =============================================================================

export function MacroCalculator({
  clientId,
  clientName,
  open,
  onOpenChange,
  onComplete,
}: MacroCalculatorProps) {
  const [currentStep, setCurrentStep] = useState<Step>('profile');

  // Profile state
  const [weight, setWeight] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [age, setAge] = useState<string>('');
  const [sex, setSex] = useState<'male' | 'female'>('male');
  const [activityLevel, setActivityLevel] = useState<ActivityLevel>('moderate');

  // Baseline state
  const [baselineSource, setBaselineSource] = useState<'formula' | 'food_log'>('formula');
  const [customTdee, setCustomTdee] = useState<string>('');

  // Goal state
  const [goal, setGoal] = useState<Goal>('fat_loss');
  const [calorieAdjustment, setCalorieAdjustment] = useState<number>(-250);

  // Macro state - can be set directly or via g/lb
  const [proteinPerLb, setProteinPerLb] = useState<number>(1.0);
  const [fatPerLb, setFatPerLb] = useState<number>(0.35);
  const [proteinOverride, setProteinOverride] = useState<string>('');
  const [fatOverride, setFatOverride] = useState<string>('');
  const [carbsOverride, setCarbsOverride] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Hooks
  const { data: clientData } = useClientForCalculator(clientId);
  const { data: baselineAnalysis } = useBaselineAnalysis(clientId);
  const createPlan = useCreateMacroPlan();
  const saveProfile = useSaveClientProfile();

  // Prefill from the client's stored profile.
  //
  // This was `useState(() => {...})` — a lazy *initializer* that runs once on
  // the first render, when `clientData` is still undefined because the query
  // hasn't resolved. The prefill therefore never applied to anyone. It must be
  // an effect keyed on the loaded data.
  //
  // Only fills empty fields, so it never clobbers something the trainer has
  // already typed while the query was in flight.
  useEffect(() => {
    if (!clientData) return;
    if (clientData.latest_weight_lbs != null) {
      setWeight((w) => w || String(clientData.latest_weight_lbs));
    }
    if (clientData.height_inches != null) {
      setHeight((h) => h || String(clientData.height_inches));
    }
    if (clientData.age != null) {
      setAge((a) => a || String(clientData.age));
    }
    if (clientData.sex) {
      setSex(clientData.sex);
    }
  }, [clientData]);

  // Build profile for calculations
  // Use sensible defaults when data isn't provided
  const profile: ClientProfile = useMemo(() => {
    const w = parseFloat(weight) || 160; // Default weight if not provided
    const h = parseFloat(height) || 68; // Default height if not provided
    const a = parseInt(age) || 30; // Default age if not provided

    return {
      weight_lbs: w,
      height_inches: h,
      age: a,
      sex,
      activity_level: activityLevel,
    };
  }, [weight, height, age, sex, activityLevel]);

  // Check if we have weight for more accurate g/lb calculations
  const hasWeight = Boolean(weight);

  // Check if we have enough data for formula-based TDEE
  const hasFullProfile = Boolean(weight && height && age);

  // Calculate TDEE from formula
  const formulaTdee = useMemo(() => {
    if (!profile) return 0;
    return calculateTDEE(profile);
  }, [profile]);

  // Get effective baseline TDEE
  const effectiveTdee = useMemo(() => {
    // Custom TDEE always takes priority if provided
    if (customTdee) {
      return parseInt(customTdee);
    }
    // Then food log data
    if (baselineSource === 'food_log' && baselineAnalysis?.estimated_tdee) {
      return baselineAnalysis.estimated_tdee;
    }
    // Then formula (only if full profile available)
    if (hasFullProfile && formulaTdee > 0) {
      return formulaTdee;
    }
    return 0;
  }, [baselineSource, baselineAnalysis, customTdee, formulaTdee, hasFullProfile]);

  // Calculate full plan
  const calculationResult = useMemo((): CalculationResult | null => {
    if (!effectiveTdee) return null;

    const settings: GoalSettings = {
      goal,
      calorie_adjustment: calorieAdjustment,
      protein_per_lb: proteinPerLb,
      fat_per_lb: fatPerLb,
    };

    const basePlan = calculateFullPlan(profile, settings, effectiveTdee);

    // Apply manual overrides if set
    const targetCalories = basePlan.targets.calories;
    const proteinG = proteinOverride ? parseInt(proteinOverride) : basePlan.targets.protein_g;
    const fatG = fatOverride ? parseInt(fatOverride) : basePlan.targets.fat_g;

    // Carbs fill remaining calories (if overridden, use that; otherwise calculate)
    let carbsG: number;
    if (carbsOverride) {
      carbsG = parseInt(carbsOverride);
    } else {
      const proteinCals = proteinG * 4;
      const fatCals = fatG * 9;
      const remainingCals = targetCalories - proteinCals - fatCals;
      carbsG = Math.max(0, Math.round(remainingCals / 4));
    }

    // Recalculate percentages
    const totalCals = (proteinG * 4) + (carbsG * 4) + (fatG * 9);
    const proteinPct = totalCals > 0 ? Math.round((proteinG * 4 / totalCals) * 100) : 0;
    const carbsPct = totalCals > 0 ? Math.round((carbsG * 4 / totalCals) * 100) : 0;
    const fatPct = totalCals > 0 ? Math.round((fatG * 9 / totalCals) * 100) : 0;

    return {
      ...basePlan,
      targets: {
        ...basePlan.targets,
        protein_g: proteinG,
        carbs_g: carbsG,
        fat_g: fatG,
      },
      macros_percentage: {
        protein: proteinPct,
        carbs: carbsPct,
        fat: fatPct,
      },
    };
  }, [profile, effectiveTdee, goal, calorieAdjustment, proteinPerLb, fatPerLb, proteinOverride, fatOverride, carbsOverride]);

  // Validation
  const validation = useMemo(() => {
    if (!calculationResult) return { valid: false, warnings: [] };
    return validatePlan(calculationResult);
  }, [calculationResult]);

  // Step navigation
  const stepIndex = STEPS.findIndex((s) => s.id === currentStep);
  const canGoNext = useMemo(() => {
    switch (currentStep) {
      case 'profile':
        // Can proceed with just weight, or skip profile entirely if using manual TDEE
        return true;
      case 'baseline':
        return effectiveTdee > 0;
      case 'goal':
        return true;
      case 'macros':
        return calculationResult !== null;
      case 'review':
        return validation.valid || validation.warnings.length === 0;
      default:
        return false;
    }
  }, [currentStep, effectiveTdee, calculationResult, validation]);

  const goNext = () => {
    const idx = STEPS.findIndex((s) => s.id === currentStep);
    if (idx < STEPS.length - 1) {
      setCurrentStep(STEPS[idx + 1].id);
    }
  };

  const goBack = () => {
    const idx = STEPS.findIndex((s) => s.id === currentStep);
    if (idx > 0) {
      setCurrentStep(STEPS[idx - 1].id);
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!calculationResult) return;

    // Persist the physical profile so it prefills next time instead of being
    // retyped for every plan. Fire-and-forget: the plan is the thing that
    // matters, and this write failing shouldn't block or look like an error.
    saveProfile.mutate({
      client_id: clientId,
      height_inches: height ? parseFloat(height) : null,
      age: age ? parseInt(age, 10) : null,
      sex,
    });

    // Create the macro plan
    await createPlan.mutateAsync({
      client_id: clientId,
      goal_type: goal,
      baseline_source: customTdee ? 'formula' : baselineSource, // Use formula source for manual entry
      baseline_tdee: effectiveTdee,
      calorie_adjustment: calorieAdjustment,
      protein_per_lb: proteinPerLb,
      fat_per_lb: fatPerLb,
      activity_level: activityLevel,
      starting_weight_lbs: weight ? parseFloat(weight) : profile.weight_lbs,
      target_calories: calculationResult.targets.calories,
      target_protein_g: calculationResult.targets.protein_g,
      target_carbs_g: calculationResult.targets.carbs_g,
      target_fat_g: calculationResult.targets.fat_g,
      target_fiber_g: calculationResult.targets.fiber_g,
      target_water_oz: calculationResult.targets.water_oz,
      notes: notes || undefined,
    });

    onOpenChange(false);
    onComplete?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Macro Calculator - {clientName}
          </DialogTitle>
          <DialogDescription>
            Create a data-driven nutrition plan with calculated macros and calorie targets.
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {STEPS.map((step, idx) => (
            <div
              key={step.id}
              className={cn(
                'flex items-center gap-2',
                idx <= stepIndex ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              <div
                className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors',
                  idx < stepIndex
                    ? 'bg-primary border-primary text-primary-foreground'
                    : idx === stepIndex
                      ? 'border-primary text-primary'
                      : 'border-muted-foreground/30'
                )}
              >
                {idx < stepIndex ? <Check className="h-4 w-4" /> : step.icon}
              </div>
              <span className="hidden sm:inline text-sm font-medium">{step.label}</span>
              {idx < STEPS.length - 1 && (
                <div className="w-8 h-0.5 bg-muted-foreground/30 mx-2" />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[300px]">
          {/* Step 1: Profile */}
          {currentStep === 'profile' && (
            <div className="space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  All fields are optional. If you know the client's maintenance calories, you can skip this step and enter them directly on the next screen.
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="weight">Current Weight (lbs) <span className="text-muted-foreground text-xs">recommended</span></Label>
                  <Input
                    id="weight"
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="180"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height">Height (inches) <span className="text-muted-foreground text-xs">optional</span></Label>
                  <Input
                    id="height"
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="70"
                  />
                  <p className="text-xs text-muted-foreground">
                    {height && `${Math.floor(parseInt(height) / 12)}'${parseInt(height) % 12}"`}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="age">Age <span className="text-muted-foreground text-xs">optional</span></Label>
                  <Input
                    id="age"
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder="30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sex <span className="text-muted-foreground text-xs">optional</span></Label>
                  <RadioGroup value={sex} onValueChange={(v) => setSex(v as 'male' | 'female')}>
                    <div className="flex gap-4">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="male" id="male" />
                        <Label htmlFor="male">Male</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="female" id="female" />
                        <Label htmlFor="female">Female</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Activity Level <span className="text-muted-foreground text-xs">optional</span></Label>
                <Select value={activityLevel} onValueChange={(v) => setActivityLevel(v as ActivityLevel)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACTIVITY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {hasFullProfile && profile && (
                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Estimated BMR:</span>
                      <span className="font-semibold">{calculateBMR(profile)} cal/day</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-muted-foreground">Estimated TDEE:</span>
                      <span className="font-semibold">{formulaTdee} cal/day</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {!hasFullProfile && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  Enter weight, height, and age to auto-calculate TDEE, or skip to enter calories manually.
                </p>
              )}
            </div>
          )}

          {/* Step 2: Baseline */}
          {currentStep === 'baseline' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {hasFullProfile
                  ? 'Choose how to establish the baseline calorie needs. Using actual food log data (when available) provides more accurate results.'
                  : 'Enter the client\'s maintenance calories, or use food log data if available.'}
              </p>

              <RadioGroup value={baselineSource} onValueChange={(v) => setBaselineSource(v as 'formula' | 'food_log')}>
                {/* Formula Option - only show if we have full profile */}
                {hasFullProfile && (
                  <Card className={cn('cursor-pointer transition-colors', baselineSource === 'formula' && 'border-primary')}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <RadioGroupItem value="formula" id="formula" className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor="formula" className="text-base cursor-pointer">
                            Calculate from Profile
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            Use Mifflin-St Jeor equation based on profile data
                          </p>
                          <div className="mt-3 p-3 bg-muted/50 rounded-md">
                            <div className="flex justify-between">
                              <span className="text-sm">Estimated TDEE:</span>
                              <span className="font-semibold">{formulaTdee} cal/day</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Food Log Option */}
                <Card className={cn(
                  'cursor-pointer transition-colors',
                  baselineSource === 'food_log' && 'border-primary',
                  !baselineAnalysis && 'opacity-60'
                )}>
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <RadioGroupItem
                        value="food_log"
                        id="food_log"
                        className="mt-1"
                        disabled={!baselineAnalysis}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="food_log" className="text-base cursor-pointer">
                            Use Food Log Data
                          </Label>
                          {baselineAnalysis && (
                            <Badge variant={
                              baselineAnalysis.confidence === 'high' ? 'default' :
                                baselineAnalysis.confidence === 'medium' ? 'secondary' : 'outline'
                            }>
                              {baselineAnalysis.confidence} confidence
                            </Badge>
                          )}
                        </div>
                        {baselineAnalysis ? (
                          <>
                            <p className="text-sm text-muted-foreground mt-1">
                              {baselineAnalysis.confidence_reason}
                            </p>
                            <div className="mt-3 p-3 bg-muted/50 rounded-md space-y-2">
                              <div className="flex justify-between">
                                <span className="text-sm">Days tracked:</span>
                                <span className="font-medium">{baselineAnalysis.days_tracked}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm">Avg intake:</span>
                                <span className="font-medium">{baselineAnalysis.average_calories} cal/day</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm">Weight change:</span>
                                <span className="font-medium">
                                  {baselineAnalysis.weight_change_lbs > 0 ? '+' : ''}
                                  {baselineAnalysis.weight_change_lbs.toFixed(1)} lbs
                                </span>
                              </div>
                              <div className="flex justify-between border-t pt-2">
                                <span className="text-sm">Estimated TDEE:</span>
                                <span className="font-semibold">{baselineAnalysis.estimated_tdee} cal/day</span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-muted-foreground mt-1">
                            Need at least 5 days of food logs to use this option.
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </RadioGroup>

              {/* Manual TDEE entry - always show, but emphasize when no formula available */}
              <Card className={cn(
                'transition-colors',
                !hasFullProfile && !baselineAnalysis && 'border-primary'
              )}>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <Label htmlFor="customTdee" className="text-base">
                      {hasFullProfile ? 'Or enter maintenance calories manually' : 'Enter Maintenance Calories'}
                    </Label>
                    <Input
                      id="customTdee"
                      type="number"
                      value={customTdee}
                      onChange={(e) => setCustomTdee(e.target.value)}
                      placeholder={hasFullProfile ? String(formulaTdee) : '2000'}
                    />
                    <p className="text-xs text-muted-foreground">
                      {hasFullProfile
                        ? 'Override the calculated value if you know the client\'s actual maintenance calories.'
                        : 'Enter the estimated daily calories to maintain current weight.'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: Goal */}
          {currentStep === 'goal' && (
            <div className="space-y-6">
              {/* Goal Selection */}
              <div className="space-y-3">
                <Label>Goal</Label>
                <RadioGroup value={goal} onValueChange={(v) => {
                  setGoal(v as Goal);
                  // Set default adjustment for goal
                  if (v === 'fat_loss') setCalorieAdjustment(-250);
                  else if (v === 'muscle_gain') setCalorieAdjustment(250);
                  else setCalorieAdjustment(0);
                }}>
                  <div className="grid grid-cols-3 gap-3">
                    {(['fat_loss', 'maintenance', 'muscle_gain'] as Goal[]).map((g) => (
                      <Card
                        key={g}
                        className={cn(
                          'cursor-pointer transition-colors',
                          goal === g && 'border-primary bg-primary/5'
                        )}
                        onClick={() => {
                          setGoal(g);
                          if (g === 'fat_loss') setCalorieAdjustment(-250);
                          else if (g === 'muscle_gain') setCalorieAdjustment(250);
                          else setCalorieAdjustment(0);
                        }}
                      >
                        <CardContent className="pt-4 text-center">
                          <RadioGroupItem value={g} id={g} className="sr-only" />
                          <Label htmlFor={g} className="cursor-pointer font-medium">
                            {GOAL_LABELS[g]}
                          </Label>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </RadioGroup>
              </div>

              {/* Aggressiveness Slider */}
              {goal !== 'maintenance' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label>Aggressiveness</Label>
                    <span className="text-sm font-medium">
                      {formatCalorieAdjustment(calorieAdjustment)}
                    </span>
                  </div>
                  <Slider
                    value={[calorieAdjustment]}
                    onValueChange={([v]) => setCalorieAdjustment(v)}
                    min={goal === 'fat_loss' ? -1000 : 100}
                    max={goal === 'fat_loss' ? -100 : 750}
                    step={50}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Conservative</span>
                    <span>Aggressive</span>
                  </div>

                  {/* Presets */}
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(CALORIE_PRESETS[goal]).map(([key, preset]) => (
                      <Button
                        key={key}
                        variant={calorieAdjustment === preset.adjustment ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCalorieAdjustment(preset.adjustment)}
                      >
                        {preset.label} ({preset.rate})
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              <Card className="bg-muted/50">
                <CardContent className="pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Baseline TDEE:</span>
                    <span className="font-medium">{effectiveTdee} cal</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Adjustment:</span>
                    <span className="font-medium">{formatCalorieAdjustment(calorieAdjustment)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-sm font-medium">Target Calories:</span>
                    <span className="font-bold text-lg">{effectiveTdee + calorieAdjustment}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span className="text-sm">Expected change:</span>
                    <span className="text-sm">{formatWeeklyChange(calculateWeeklyChange(calorieAdjustment))}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 4: Macros */}
          {currentStep === 'macros' && calculationResult && (
            <div className="space-y-6">
              {!hasWeight && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Using estimated body weight (160 lbs) for g/lb calculations. Add the client's weight in step 1 for more accurate macros.
                  </AlertDescription>
                </Alert>
              )}

              <p className="text-sm text-muted-foreground">
                Enter exact gram values or leave blank to use calculated defaults based on body weight.
              </p>

              {/* Macro Inputs Grid */}
              <div className="grid grid-cols-3 gap-4">
                {/* Protein */}
                <div className="space-y-2">
                  <Label htmlFor="protein" className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-chart-2" />
                    Protein (g)
                  </Label>
                  <Input
                    id="protein"
                    type="number"
                    value={proteinOverride || calculationResult.targets.protein_g}
                    onChange={(e) => setProteinOverride(e.target.value)}
                    placeholder={String(calculationResult.targets.protein_g)}
                  />
                  <p className="text-xs text-muted-foreground">
                    ~{proteinPerLb}g/lb
                  </p>
                </div>

                {/* Carbs */}
                <div className="space-y-2">
                  <Label htmlFor="carbs" className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-chart-3" />
                    Carbs (g)
                  </Label>
                  <Input
                    id="carbs"
                    type="number"
                    value={carbsOverride || calculationResult.targets.carbs_g}
                    onChange={(e) => setCarbsOverride(e.target.value)}
                    placeholder={String(calculationResult.targets.carbs_g)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Remaining cals
                  </p>
                </div>

                {/* Fat */}
                <div className="space-y-2">
                  <Label htmlFor="fat" className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-chart-1" />
                    Fat (g)
                  </Label>
                  <Input
                    id="fat"
                    type="number"
                    value={fatOverride || calculationResult.targets.fat_g}
                    onChange={(e) => setFatOverride(e.target.value)}
                    placeholder={String(calculationResult.targets.fat_g)}
                  />
                  <p className="text-xs text-muted-foreground">
                    ~{fatPerLb}g/lb
                  </p>
                </div>
              </div>

              {/* Quick reset button */}
              {(proteinOverride || fatOverride || carbsOverride) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setProteinOverride('');
                    setFatOverride('');
                    setCarbsOverride('');
                  }}
                  className="text-muted-foreground"
                >
                  Reset to calculated values
                </Button>
              )}

              {/* Macro Distribution Visual */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Macro Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex h-4 rounded-full overflow-hidden">
                    <div
                      className="bg-chart-2"
                      style={{ width: `${calculationResult.macros_percentage.protein}%` }}
                    />
                    <div
                      className="bg-chart-3"
                      style={{ width: `${calculationResult.macros_percentage.carbs}%` }}
                    />
                    <div
                      className="bg-chart-1"
                      style={{ width: `${calculationResult.macros_percentage.fat}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-xs">
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-chart-2" />
                      Protein {calculationResult.macros_percentage.protein}%
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-chart-3" />
                      Carbs {calculationResult.macros_percentage.carbs}%
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-chart-1" />
                      Fat {calculationResult.macros_percentage.fat}%
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Calorie breakdown */}
              <div className="p-3 bg-muted/50 rounded-lg text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total from macros:</span>
                  <span className="font-medium">
                    {(calculationResult.targets.protein_g * 4) +
                      (calculationResult.targets.carbs_g * 4) +
                      (calculationResult.targets.fat_g * 9)} cal
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Target:</span>
                  <span className="font-medium">{calculationResult.targets.calories} cal</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {currentStep === 'review' && calculationResult && (
            <div className="space-y-4">
              {/* Warnings */}
              {validation.warnings.length > 0 && (
                <Alert variant="destructive">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-disc pl-4 space-y-1">
                      {validation.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Summary Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Plan Summary</CardTitle>
                  <CardDescription>
                    {GOAL_LABELS[goal]} • {formatWeeklyChange(calculationResult.weekly_change_lbs)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold">{calculationResult.targets.calories}</div>
                      <div className="text-xs text-muted-foreground">Calories</div>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-chart-2">{calculationResult.targets.protein_g}g</div>
                      <div className="text-xs text-muted-foreground">Protein</div>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-chart-3">{calculationResult.targets.carbs_g}g</div>
                      <div className="text-xs text-muted-foreground">Carbs</div>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-chart-1">{calculationResult.targets.fat_g}g</div>
                      <div className="text-xs text-muted-foreground">Fat</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Fiber:</span>
                      <span className="text-sm font-medium">{calculationResult.targets.fiber_g}g</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Water:</span>
                      <span className="text-sm font-medium">{calculationResult.targets.water_oz} oz</span>
                    </div>
                  </div>

                  <div className="pt-2 border-t">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Baseline ({baselineSource === 'food_log' ? 'from logs' : 'formula'}):</span>
                      <span>{effectiveTdee} cal</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Adjustment:</span>
                      <span>{formatCalorieAdjustment(calorieAdjustment)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes about this plan..."
                  rows={3}
                />
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={goBack}
            disabled={stepIndex === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          {currentStep === 'review' ? (
            <Button
              onClick={handleSubmit}
              disabled={createPlan.isPending || !validation.valid && validation.warnings.length > 0}
            >
              {createPlan.isPending ? 'Creating...' : 'Create Plan'}
            </Button>
          ) : (
            <Button onClick={goNext} disabled={!canGoNext}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper to calculate weekly change (imported but also need here)
function calculateWeeklyChange(adjustment: number): number {
  return (adjustment * 7) / 3500;
}
